import { useState } from 'react';
import { useHistory } from 'react-router';
import { useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { submitOrder } from 'services/resources/orders';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';
import {
  useGetOrderByUUIDQuery,
  useUpdateOrderMutation,
} from 'store/apis/orderApi';
import { useGetSamplesByOrderIdQuery } from 'store/apis/samplesApi';
import { useQueryError } from 'utilities/hooks/useQueryError/useQueryError';
import { userDetailsSelector } from 'store/slices/userDetailsSlice';

import {
  PLACE_ORDER_REVIEW_FEEDBACK_URL,
  PLACE_PATHOLOGY_CONSULTATION_ORDER_FORM_INSTANCE_URL,
} from 'constants/urls';
import {
  DEMO_USER_EMAIL,
  LAST_ORDER_FORM_PAGE,
  ORDER_FETCHING_ERROR_MESSAGE,
} from 'components/OrderForm/constants';
import {
  SUBMIT_ORDER_ERROR_MESSAGE,
  UPDATE_ORDER_ERROR_MESSAGE,
} from 'constants/errorMessages';
import { DEFAULT_EXTRA_MESSAGE } from 'utilities/hooks/useSnackbar/constants';

export const useGetReviewViewData = () => {
  const {
    params: { orderUUID },
  } = useRouteMatch();
  const { email } = useSelector(userDetailsSelector);
  const { data: order, error } = useGetOrderByUUIDQuery(orderUUID, {
    skip: !orderUUID,
  });
  const { data: samples } = useGetSamplesByOrderIdQuery(
    { orderUUID },
    {
      skip: !orderUUID,
    },
  );

  const isDropbox = !!order?.dopbox;
  const isDemoUser = email === DEMO_USER_EMAIL;

  useQueryError(error, ORDER_FETCHING_ERROR_MESSAGE);

  return {
    orderUUID,
    order,
    samples,
    isDropbox,
    isDemoUser,
  };
};

export const useReviewViewSubmit = ({ orderUUID, order }) => {
  const [submitting, setSubmitting] = useState(false);

  const history = useHistory();
  const { showError } = useSnackbar();

  const [updateOrderMutation] = useUpdateOrderMutation();

  const handleReviewSubmit = async () => {
    try {
      setSubmitting(true);
      const response = await submitOrder({ orderUUID });

      if (!response) {
        return;
      }

      const { pathology_consultations } = response;
      const pathologyConsultationOrderUUID = pathology_consultations?.[0]?.uuid;
      const orderID = order.name.replace('Order ', '');

      if (
        order.requested_pathology_consultation &&
        !!pathologyConsultationOrderUUID
      ) {
        const pcFormUrl =
          PLACE_PATHOLOGY_CONSULTATION_ORDER_FORM_INSTANCE_URL.replace(
            ':consultUUID',
            pathologyConsultationOrderUUID,
          );

        history.push(pcFormUrl, { from: history.location.pathname });
      } else {
        history.push(
          PLACE_ORDER_REVIEW_FEEDBACK_URL.replace(':orderID', orderID),
        );
      }
    } catch (e) {
      showError(`${SUBMIT_ORDER_ERROR_MESSAGE} \n ${DEFAULT_EXTRA_MESSAGE}`);
    }

    try {
      await updateOrderMutation({
        orderUUID,
        postParams: { last_order_form_page: LAST_ORDER_FORM_PAGE.REVIEW },
      });
    } catch (e) {
      showError(`${UPDATE_ORDER_ERROR_MESSAGE} \n ${DEFAULT_EXTRA_MESSAGE}`);
    }
  };

  return { submitting, handleReviewSubmit };
};
